import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { buildNasLookup, findNasMatch } from '../copyrightRegistry';

const NasContext = createContext(null);

export const formatBytes = (bytes = 0) => {
    if (!bytes) return '0 MB';
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const exponent = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    const value = bytes / 1024 ** exponent;
    return `${value >= 10 || exponent < 3 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`;
};

export function NasProvider({ db, auth, appId, currentUid, children }) {
    const [items, setItems] = useState([]);
    const [uploads, setUploads] = useState({});
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!db || !currentUid) return undefined;
        const base = ['artifacts', appId, 'public', 'data'];
        // The index is split into shard docs; 'main' is the pre-shard layout kept until the first scan.
        const unsubIndex = onSnapshot(collection(db, ...base, 'nas_index'), snap => {
            const byId = new Map();
            const docs = [...snap.docs].sort((a, b) => (a.id === 'main' ? -1 : b.id === 'main' ? 1 : 0));
            docs.forEach(item => (item.data().items || []).forEach(entry => byId.set(entry.id, entry)));
            setItems([...byId.values()].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)));
            setLoaded(true);
        }, error => { console.error('[NAS index]', error); setLoaded(true); });
        const unsubUploads = onSnapshot(collection(db, ...base, 'nas_uploads'), snap => {
            const next = {};
            snap.docs.forEach(item => { next[item.id] = { id: item.id, ...item.data() }; });
            setUploads(next);
        }, error => console.error('[NAS uploads]', error));
        return () => { unsubIndex(); unsubUploads(); };
    }, [db, appId, currentUid]);

    const callApi = useCallback(async (action, payload = {}) => {
        const token = await auth.currentUser?.getIdToken();
        if (!token) throw new Error('Bạn cần đăng nhập lại.');
        const response = await fetch('/api/nas', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ action, ...payload })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Không kết nối được kho NAS.');
        return data;
    }, [auth]);

    // Admin-only: live state of the old-data scan and its review lists.
    const watchScan = useCallback(callback => onSnapshot(
        doc(db, 'artifacts', appId, 'public', 'data', 'nas_scan', 'state'),
        snap => callback(snap.exists() ? snap.data() : null),
        error => { console.error('[NAS scan]', error); callback(null); }
    ), [db, appId]);

    const lookup = useMemo(() => buildNasLookup(items), [items]);

    const findForTask = useCallback(task => findNasMatch(task, lookup).item, [lookup]);

    // Open the tab synchronously so the popup blocker allows it, then point it at the NAS link.
    const openDownload = useCallback(async archiveId => {
        const win = window.open('', '_blank');
        try {
            const { url } = await callApi('download-link', { archiveId });
            if (win) win.location.href = url;
            else window.location.href = url;
        } catch (error) {
            if (win) win.close();
            window.alert(error.message);
        }
    }, [callApi]);

    const suggestions = useMemo(() => ({
        genres: [...new Set(items.map(item => item.genre).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'vi')),
        authors: [...new Set(items.map(item => item.author).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'vi')),
        works: items.filter(item => !item.quarantined && item.kind !== 'file')
    }), [items]);

    const value = useMemo(() => ({ items, uploads, loaded, callApi, findForTask, openDownload, suggestions, watchScan }),
        [items, uploads, loaded, callApi, findForTask, openDownload, suggestions, watchScan]);

    return <NasContext.Provider value={value}>{children}</NasContext.Provider>;
}

export const useNas = () => useContext(NasContext);
